import axiosInstance from '../../axios/axiosInstance'
import { wordContextLanguage } from '../../i18n/localizedContent'

/** The two fallback answers of "Explain in context", by the code the API sends in X-Error-Code. */
export const WORD_CONTEXT_CODES = ['WORD_CONTEXT_EMPTY', 'WORD_CONTEXT_FAILED'] as const
export type WordContextCode = (typeof WORD_CONTEXT_CODES)[number]

// Literal keys, so the key scan (i18n/keyUsage.ts) sees them.
export const WORD_CONTEXT_MESSAGE_KEYS: Record<WordContextCode, string> = {
  WORD_CONTEXT_EMPTY: 'reader:wordContext.WORD_CONTEXT_EMPTY',
  WORD_CONTEXT_FAILED: 'reader:wordContext.WORD_CONTEXT_FAILED',
}

export interface WordContextAnswer {
  /** The explanation, or the API's English fallback text. */
  text: string
  /** Set when the answer is one of the fallbacks: show the translated copy for it instead. */
  code: WordContextCode | null
}

export const wordContextBody = (sentence: string | undefined, word: string, language?: unknown) => {
  const lang = wordContextLanguage(language)
  return lang ? { sentence, word, lang } : { sentence, word }
}

const readCode = (headers: any): WordContextCode | null => {
  const raw = typeof headers?.get === 'function' ? headers.get('x-error-code') : headers?.['x-error-code']
  return (WORD_CONTEXT_CODES as readonly string[]).includes(raw) ? (raw as WordContextCode) : null
}

/**
 * "Explain in context" (`POST words/get-word-context`). Asks for the answer in the interface
 * language when it is a learner language (the body's `lang`; English otherwise). Resolves to null
 * when the request fails or the body is not text.
 */
export const fetchWordContext = async (
  sentence: string | undefined,
  word: string,
  language?: unknown,
): Promise<WordContextAnswer | null> => {
  try {
    const res = await axiosInstance.post('words/get-word-context', wordContextBody(sentence, word, language))
    const code = readCode(res?.headers)
    if (code) return { text: typeof res?.data === 'string' ? res.data : '', code }
    return typeof res?.data === 'string' ? { text: res.data, code: null } : null
  } catch {
    return null
  }
}
